// Turns a flat list of expenses into the group/ungrouped split that both the
// employee's expense page and the approval queue render. The API decorates each
// expense with group_id / group_name / group_submitted_at, so groups are
// derived from the same list rather than fetched separately — there is nothing
// to keep in sync, and a group with no expenses simply doesn't appear.

const toTime = (value) => {
  if (!value) return 0;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

// A group is "settled" once none of its expenses are awaiting a decision, which
// is what lets the card show progress without a status of its own.
const summarizeStatuses = (expenses) => {
  const counts = { pending: 0, approved: 0, rejected: 0 };
  expenses.forEach((expense) => {
    const status = String(expense?.status || '').toLowerCase();
    if (status in counts) counts[status] += 1;
  });
  return counts;
};

export const buildExpenseGroups = (expenses) => {
  const rows = Array.isArray(expenses) ? expenses : [];
  const ungrouped = [];
  const byId = new Map();

  rows.forEach((expense) => {
    const groupId = expense?.group_id;
    if (!groupId) {
      ungrouped.push(expense);
      return;
    }

    if (!byId.has(groupId)) {
      byId.set(groupId, {
        id: groupId,
        name: expense.group_name || 'Expense group',
        submittedAt: expense.group_submitted_at || null,
        employeeId: expense.employee_id || null,
        expenses: [],
      });
    }
    byId.get(groupId).expenses.push(expense);
  });

  const groups = Array.from(byId.values()).map((group) => {
    const counts = summarizeStatuses(group.expenses);
    return {
      ...group,
      isSubmitted: Boolean(group.submittedAt),
      count: group.expenses.length,
      total: group.expenses.reduce((sum, expense) => sum + (Number(expense.amount) || 0), 0),
      statusCounts: counts,
      // Newest member first, matching how the flat lists are ordered.
      sortTime: Math.max(...group.expenses.map((expense) => toTime(expense.created_at)), 0),
    };
  });

  groups.sort((a, b) => b.sortTime - a.sortTime);

  return { groups, ungrouped };
};

// Flattens the group/ungrouped split back into one ordered row list for the
// table views. A group contributes a header row followed by its expenses, so a
// batch reads as a labelled run of rows instead of separate card UI beside the
// table. Groups and loose expenses interleave on the same newest-first order the
// flat list uses, so a batch doesn't jump to the top of the queue.
export const flattenExpenseGroups = ({ groups = [], ungrouped = [] } = {}) => {
  const entries = [
    ...groups.map((group) => ({ sortTime: group.sortTime, group })),
    ...ungrouped.map((expense) => ({ sortTime: toTime(expense?.created_at), expense })),
  ];

  entries.sort((a, b) => b.sortTime - a.sortTime);

  const rows = [];
  entries.forEach(({ group, expense }) => {
    if (expense) {
      rows.push(expense);
      return;
    }

    rows.push({
      // Marks the row for the table's cell renderers, the same way isTotalRow
      // does. The id keeps rowKey unique against the expenses around it.
      isGroupRow: true,
      id: `group-${group.id}`,
      group,
    });
    group.expenses.forEach((member, index) => {
      rows.push({
        ...member,
        // Members carry their group so the table can tint the run and close it
        // off at the last one.
        groupRowId: group.id,
        isLastInGroup: index === group.expenses.length - 1,
      });
    });
  });

  return rows;
};

// Same newest-first interleave as flattenExpenseGroups, but the members stay
// nested inside their group instead of being spread into sibling rows. The
// kanban board needs that shape: it draws a batch as one physical block of
// cards rather than a labelled run of table rows.
export const buildExpenseGroupBlocks = (expenses) => {
  const { groups, ungrouped } = buildExpenseGroups(expenses);

  const entries = [
    ...groups.map((group) => ({ sortTime: group.sortTime, group })),
    ...ungrouped.map((expense) => ({ sortTime: toTime(expense?.created_at), expense })),
  ];

  entries.sort((a, b) => b.sortTime - a.sortTime);

  return entries.map(({ group, expense }) => (
    group
      ? { type: 'group', key: `group-${group.id}`, group }
      : { type: 'expense', key: `expense-${expense.id}`, expense }
  ));
};

// One-line description of where a group stands, for the card and the modal.
export const describeExpenseGroup = (group) => {
  if (!group) return '';
  if (!group.isSubmitted) {
    return group.count === 0 ? 'Not submitted yet' : 'Draft — not submitted yet';
  }

  const { pending, approved, rejected } = group.statusCounts;
  const parts = [];
  if (pending > 0) parts.push(`${pending} awaiting review`);
  if (approved > 0) parts.push(`${approved} approved`);
  if (rejected > 0) parts.push(`${rejected} rejected`);
  return parts.join(' • ') || 'Submitted';
};
